import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeftIcon, BadgeCheckIcon, BedDoubleIcon, CalendarIcon, CheckIcon, FootprintsIcon, HomeIcon, MessageSquareIcon, StarIcon, TriangleAlertIcon, UsersIcon } from 'lucide-react';
import { Panel } from '../../components/ui/Panel';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { EmptyState } from '../../components/ui/EmptyState';
import { VerificationBadge } from '../../components/housing/VerificationBadge';
import { ApplicationDialog } from '../../components/housing/ApplicationDialog';
import { ReportControls } from '../../components/trust/ReportControls';
import { listingById } from '../../data/housing';
import { naira, pluralize } from '../../utils/format';
import { verificationCopy } from '../../utils/housing';
const REVIEWS = [{
  id: 'rev-1',
  author: 'Uche M.',
  detail: 'Power has been steady all session and the study lounge is genuinely quiet in the evenings. Water pressure drops occasionally on the top floor.',
  rating: 5,
  stay: 'Lived here 2 sessions'
}, {
  id: 'rev-2',
  author: 'Blessing I.',
  detail: 'Management responds quickly to maintenance requests. Rooms are smaller than the photos suggest, so visit first.',
  rating: 4,
  stay: 'Lived here 1 session'
}];
export function ListingDetail() {
  const {
    listingId
  } = useParams<{
    listingId: string;
  }>();
  const listing = listingId ? listingById(listingId) : undefined;
  const [applyOpen, setApplyOpen] = useState(false);
  if (!listing) {
    return <div className="rounded-card border border-line bg-surface" data-id="element-9567" data-mp="1e7rpat9lp62a">
        <EmptyState icon={<HomeIcon className="h-4 w-4" data-id="element-9569" data-mp="1a77pfhxn7ocg" />} title="Listing not available" description="This listing may have been removed by its owner or suspended by Campuzon moderation." action={<Link to="/housing" data-id="element-9570" data-mp="3bzw0uekfxi3b">
              <Button variant="secondary" data-id="element-9571" data-mp="1exopfhyyg51c">Back to housing search</Button>
            </Link>} data-id="element-9568" data-mp="1a6g7h9wyxp4n" />
      </div>;
  }
  const trust = verificationCopy(listing);
  return <div className="space-y-6" data-id="element-9572" data-mp="1jycp0s191u76">
      <Link to="/housing" className="inline-flex items-center gap-1.5 text-[13px] font-medium text-ink-muted transition-colors duration-150 ease-out hover:text-ink" data-id="element-9573" data-mp="1ovcxtaphhb1z">
        <ArrowLeftIcon className="h-3.5 w-3.5" data-id="element-9574" data-mp="1xwub6lbiaymz" />
        Back to results
      </Link>

      <div className="grid gap-6 lg:grid-cols-3" data-id="element-9575" data-mp="22v47iz8yw5a6">
        <div className="space-y-6 lg:col-span-2" data-id="element-9576" data-mp="22tt3cybm0vij">
          <img src={listing.image} alt={`${listing.name} — ${listing.kind}`} className="h-64 w-full rounded-card object-cover sm:h-80" data-id="element-9577" data-mp="27mdwryqlt6lj" />

          <div data-id="element-9578" data-mp="2cn1nskeu1nfr">
            <div className="flex flex-wrap items-start justify-between gap-3" data-id="element-9579" data-mp="2hkjwc9oxu0n8">
              <div className="min-w-0" data-id="element-9580" data-mp="2higyx4lgh58h">
                <h1 className="text-2xl font-semibold tracking-tight text-ink" data-id="element-9581" data-mp="2lmx0wury7sif">{listing.name}</h1>
                <p className="mt-1.5 text-sm text-ink-muted" data-id="element-9582" data-mp="9ys3p4j9ht45">
                  {listing.kind} · {listing.address}
                </p>
              </div>
              <VerificationBadge state={listing.verification} data-id="element-9583" data-mp="5zay8b3u4vyz" />
            </div>

            <dl className="mt-5 grid grid-cols-2 gap-x-6 gap-y-4 border-y border-line py-4 sm:grid-cols-4" data-id="element-9584" data-mp="y3r8e9io0td">
              {[{
              icon: <FootprintsIcon className="h-4 w-4" data-id="element-9585" data-mp="tmsizmoomtsg" />,
              label: 'To campus',
              value: `${listing.minutesToCampus} min`
            }, {
              icon: <UsersIcon className="h-4 w-4" data-id="element-9586" data-mp="jvxwqibfru70" />,
              label: 'Occupancy',
              value: listing.occupants === 1 ? 'Single' : `${listing.occupants} per room`
            }, {
              icon: <BedDoubleIcon className="h-4 w-4" data-id="element-9587" data-mp="1cdsc9037mprj" />,
              label: 'Beds free',
              value: String(listing.bedsAvailable)
            }, {
              icon: <CalendarIcon className="h-4 w-4" data-id="element-9588" data-mp="s3hhk6mmio1a" />,
              label: 'Available',
              value: listing.availableFrom
            }].map(item => <div key={item.label} data-id="element-9589" data-mp="i74nh7n62860">
                  <dt className="flex items-center gap-1.5 text-[12px] text-ink-subtle" data-id="element-9590" data-mp="i87gpm7yomp3">
                    {item.icon}
                    {item.label}
                  </dt>
                  <dd className="cz-num mt-1 text-[13px] font-semibold text-ink" data-id="element-9591" data-mp="1avabq4dfifal">{item.value}</dd>
                </div>)}
            </dl>

            <p className="mt-5 text-sm leading-7 text-ink-muted" data-id="element-9592" data-mp="1158jd0e1mapv">{listing.description}</p>
          </div>

          <Panel title="Facilities" data-id="element-9593" data-mp="163ighgjbvle4">
            <ul className="grid grid-cols-2 gap-x-6 gap-y-2.5 sm:grid-cols-3" data-id="element-9594" data-mp="162h51ayegwvv">
              {listing.facilities.map(facility => <li key={facility} className="flex items-center gap-2 text-[13px] text-ink" data-id="element-9595" data-mp="1trtf0d1jx8z8">
                  <CheckIcon className="h-3.5 w-3.5 shrink-0 text-brand-700" data-id="element-9596" data-mp="1ym5zmqerkqck" />
                  {facility}
                </li>)}
            </ul>
          </Panel>

          <Panel title="House rules" data-id="element-9597" data-mp="3ebeyfvha9ona">
            <ul className="space-y-2 text-[13px] text-ink-muted" data-id="element-9598" data-mp="3s2tv35445oct">
              {listing.rules.map(rule => <li key={rule} data-id="element-9599" data-mp="3o0yktb47g0mw">· {rule}</li>)}
            </ul>
          </Panel>

          <Panel title={`Reviews (${listing.reviewCount})`} description={`${listing.rating.toFixed(1)} average from students who have lived here`} bodyClassName="p-0" data-id="element-9600" data-mp="30bdwecpa8b68">
            <ul className="divide-y divide-line" data-id="element-9601" data-mp="39dn7b3ogq6k5">
              {REVIEWS.map(review => <li key={review.id} className="px-5 py-4" data-id="element-9602" data-mp="34d7tyc46kz26">
                  <div className="flex items-center justify-between gap-3" data-id="element-9603" data-mp="34fu1wcuzhgkx">
                    <p className="text-[13px] font-semibold text-ink" data-id="element-9604" data-mp="okidroxzzdqk">{review.author}</p>
                    <span className="cz-num inline-flex items-center gap-1 text-[13px] text-ink-muted" data-id="element-9605" data-mp="jmqjwpdslhjq">
                      <StarIcon className="h-3.5 w-3.5" data-id="element-9606" data-mp="3deynj2vkl9e3" />
                      {review.rating}.0
                    </span>
                  </div>
                  <p className="mt-1.5 text-[13px] leading-6 text-ink-muted" data-id="element-9607" data-mp="3mg6c2t8aavuz">{review.detail}</p>
                  <p className="mt-1.5 text-[12px] text-ink-subtle" data-id="element-9608" data-mp="3rer96js8ozf7">{review.stay}</p>
                </li>)}
            </ul>
          </Panel>
        </div>

        <div className="space-y-6" data-id="element-9609" data-mp="2umnqhpnlk5nw">
          <div className="sticky top-32 space-y-6" data-id="element-9610" data-mp="2ypu3rh94mx3v">
            <Panel data-id="element-9611" data-mp="2yqlo1vx9x8eu">
              <p className="cz-num text-2xl font-semibold tracking-tight text-ink" data-id="element-9612" data-mp="2ynzl69lbvh2y">
                {naira(listing.rentPerYear)}
              </p>
              <p className="mt-1 text-[13px] text-ink-muted" data-id="element-9613" data-mp="38h6ug5j6vh8b">
                per year · {listing.roomType} · deposit {listing.depositMonths} month
                {listing.depositMonths > 1 ? 's' : ''}
              </p>
              <div className="mt-4 space-y-2" data-id="element-9614" data-mp="2fy9lp96me74p">
                <Button variant="primary" className="w-full" onClick={() => setApplyOpen(true)} data-id="element-9615" data-mp="28g34dr76agx5">
                  Apply for this room
                </Button>
                <Button variant="secondary" className="w-full" icon={<MessageSquareIcon className="h-4 w-4" data-id="element-9617" data-mp="1yk8fwfg9prmg" />} data-id="element-9616" data-mp="23i1pligs53rh">
                  Message the operator
                </Button>
              </div>
              <p className="cz-num mt-3 text-[12px] text-ink-subtle" data-id="element-9618" data-mp="1piiovaqasup7">
                {pluralize(listing.bedsAvailable, 'bed')} currently available
              </p>
            </Panel>

            <div className={`rounded-card border px-5 py-4 ${listing.verification === 'verified' ? 'border-ok-100 bg-ok-50' : listing.verification === 'pending' ? 'border-warn-100 bg-warn-50' : 'border-line bg-surface'}`} data-id="element-9619" data-mp="1fqw8kzz3s5fl">
              <p className="flex items-center gap-2 text-[13px] font-semibold text-ink" data-id="element-9620" data-mp="11u8ydvi53irq">
                {listing.verification === 'verified' ? <BadgeCheckIcon className="h-4 w-4 text-ok-700" data-id="element-9621" data-mp="2ummcoju4zjm0" /> : <TriangleAlertIcon className="h-4 w-4 text-warn-700" data-id="element-9622" data-mp="2gonzhhrjxt83" />}
                {trust.label}
              </p>
              <p className="mt-1.5 text-[12px] leading-5 text-ink-muted" data-id="element-9623" data-mp="2knoh4wyampti">{trust.note}</p>
              {listing.verification === 'verified' && <ul className="mt-3 space-y-1.5" data-id="element-9624" data-mp="2bpbfd4s2f097">
                  {listing.verifiedChecks.map(check => <li key={check} className="flex items-center gap-2 text-[12px] text-ink-muted" data-id="element-9625" data-mp="1x0opi94taq3q">
                      <CheckIcon className="h-3 w-3 text-ok-700" data-id="element-9626" data-mp="21y8gm9mlyxd3" />
                      {check} checked
                    </li>)}
                </ul>}
            </div>

            <Panel title="Listed by" data-id="element-9627" data-mp="1syletmpyvpji">
              <p className="text-[13px] font-semibold text-ink" data-id="element-9628" data-mp="1sxip3i4drt56">{listing.manager.name}</p>
              <p className="mt-0.5 text-[13px] text-ink-muted" data-id="element-9629" data-mp="19d1u6ca9zbpu">{listing.manager.role}</p>
              <Badge className="mt-2.5" data-id="element-9630" data-mp="14eqkc1jzd17m">{listing.manager.responseTime}</Badge>
              <div className="mt-4 border-t border-line pt-3" data-id="element-9631" data-mp="1idghtiapp2wi">
                <ReportControls subject={listing.name} surface="listing" size="sm" data-id="element-9632" data-mp="1ebmm7ofdip37" />
              </div>
            </Panel>
          </div>
        </div>
      </div>

      <ApplicationDialog listing={listing} open={applyOpen} onClose={() => setApplyOpen(false)} data-id="element-9633" data-mp="1s9tdglpthqtw" />
    </div>;
}